// doctor-frontend/src/App.js
// FIX (Dec 2025):
// - Remove Router basename to prevent double-tenant-prefix navigation ("/t/slug/t/slug/...").
// - Provide BOTH tenant-prefixed and non-tenant route trees (backward compatible; prevents "logout" on sidebar click).
// - Keep existing routes/guards intact.
// - ADD alias: /intern/activity/guided/:id → ActivityGuidedRun (prevents NotFound redirects)
//
// PATCH (Jan 2026):
// - Root "/" should NOT force owner portal on localhost.
//   On localhost, redirect "/" → /t/aiimsjodhpur-cmfm/cmfm/login (tenant default).
//   On production, keep "/" → /owner/login (no regression).
//
// PATCH (Jan 2026 - alias standardization):
// - Standardize CMFM login route to: /t/:tenantSlug/cmfm/login (preferred)
// - Redirect old alias routes:
//   /t/:tenantSlug/cmfm-login  -> /t/:tenantSlug/cmfm/login
//   /cmfm-login                -> /cmfm/login
//   /t/:tenantSlug/login-qr & /t/:tenantSlug/cmfm-login-qr -> /t/:tenantSlug/cmfm/login-qr
//   /login-qr & /cmfm/login-qr -> /cmfm/login-qr (or /cmfm/login-qr as preferred)
//
// PATCH (Jul 2026 - no regression):
// - Harden intern/admin route guarding for JR/SR/HOD/Owner aliases and sessionStorage.
// - Add guided-activity route aliases for run/continue/edit/evidence/review/signoff paths.
// - Add tenant-preserving activity/approval aliases so older buttons do not fall into NotFound.

import React, { useMemo, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

import CMFMLoginRegister from './pages/CMFMLoginRegister';
import CMFMLoginQR from './pages/CMFMLoginQR';

import LayoutWithSidebarAndHeader from './components/LayoutWithSidebarAndHeader';
import DashboardLanding from './pages/DashboardLanding';

// ✅ Shared guard (accepts authToken/token/cmfmToken/cmfm_token + loggedInUser fallback; does NOT accept citizenToken)
import ProtectedRoute from './components/ProtectedRoute';

// Groups
import GroupClinical from './pages/GroupClinical';
import GroupOperations from './pages/GroupOperations';
import GroupAcademics from './pages/GroupAcademics';
import GroupTelemedicine from './pages/GroupTelemedicine';
import GroupCitizen from './pages/GroupCitizen';

// LGBTQIA+
import GroupLGBTQIA from './pages/GroupLGBTQIA';
import LGBTQIAIntakeForm from './pages/lgbtqia/IntakeForm';
import LGBTQIACareBundle from './pages/lgbtqia/CareBundle';
import LGBTQIAResources from './pages/lgbtqia/Resources';

// Citizen/visit editors (doctor portal internal workflows)
import CitizenRegistrationForm from './components/forms/CitizenRegistrationForm';
import ApprovalsDashboard from './pages/ApprovalDashboard';
import CitizenMedicalHistory from './pages/citizen/CitizenMedicalHistory';
import CitizenExam from './pages/citizen/CitizenExam';
import CitizenInvestigations from './pages/citizen/CitizenInvestigations';
import CitizenPrescriptions from './pages/citizen/CitizenPrescriptions';
import VisitDetails from './pages/VisitDetails';
import VisitSummary from './pages/citizen/VisitSummary';
import PreviousVisitsList from './pages/citizen/PreviousVisitsList';
import CitizenUploadDocuments from './pages/citizen/CitizenUploadDocuments';
import AddVisit from './pages/AddVisit';
import VisitWorkspace from './pages/visit/VisitWorkspace';
import SocialHouseholdContext from './pages/citizen/SocialHouseholdContext';

// DEO
import DEODashboard from './pages/DEODashboard';
import DEOVisitWorkflow from './pages/DEOVisitWorkflow';

// Context & sync
import { CitizenProvider } from './CitizenContext';
import CitizenRouteSync from './components/CitizenRouteSync';

// Clinical services
import GeriatricClinic from './pages/clinical/GeriatricClinic';
import AdolescentClinic from './pages/clinical/AdolescentClinic';
import MedicalCamps from './pages/clinical/MedicalCamps';
import OccupationalHealth from './pages/clinical/OccupationalHealth';
import RMNCHServices from './pages/clinical/RMNCHServices';
import ClinicalServicesReport from './pages/clinical/ClinicalServicesReport';

// Centre Services Hub
import CHCCoordination from './pages/operations/CHCCoordination';
import CentreBoard from './pages/operations/CentreBoard';

// Ops pages
import DailyOPDCensus from './pages/operations/DailyOPDCensus';

// Approval editor
import ApprovalEditor from './pages/approval/ApprovalEditor';

/* ---------- Owner pages ---------- */
import TenantsList from './pages/owner/TenantsList';
import TenantEdit from './pages/owner/TenantEdit';
import OwnerLogin from './pages/owner/OwnerLogin';
import ProtectedOwnerRoute from './components/owner/ProtectedOwnerRoute';
import OwnerHome from './pages/owner/OwnerHome';
import SubjectsHome from './pages/owner/SubjectsHome';
import SubjectDetail from './pages/owner/SubjectDetail';
import CollegesHome from './pages/owner/CollegesHome.jsx';
import CollegeSubjects from './pages/owner/CollegeSubjects.jsx';

import BrandingProvider from './context/Branding';

/* ---------- Profiles ---------- */
import ProfileMe from './pages/profile/ProfileMe';
import ProfileEditor from './pages/profile/ProfileEditor';

/* ---------- Intern module pages (existing) ---------- */
import ActivityLog from './pages/intern/ActivityLog';
import PerformanceSelfEval from './pages/intern/PerformanceSelfEval';
import RosterView from './pages/intern/RosterView';
import InternReviewQueue from './pages/intern/InternReviewQueue';

/* ---------- SR→Faculty Performance Review pages ---------- */
import PerformanceReviewNew from './pages/intern/perf/PerformanceReviewNew';
import PerformanceReviewQueue from './pages/intern/perf/PerformanceReviewQueue';
import PerformanceReviewDetail from './pages/intern/perf/PerformanceReviewDetail';

/* ---------- Guided Activity flow (templates/observer) ---------- */
import ActivityStartWizard from './pages/intern/ActivityStartWizard';
import ActivityGuidedRun from './pages/intern/ActivityGuidedRun';
import ObserverSignoff from './pages/intern/ObserverSignoff';
import ReturnedActivities from './pages/intern/ReturnedActivities';

/* ---------- Guidelines Library ---------- */
import GuidelinesLibrary from './pages/intern/GuidelinesLibrary';

/* ---------- Roster Manager (for non-interns) ---------- */
import RosterManager from './pages/roster/RosterManager';

/* ---------- Master Intern Directory + Activities Manager ---------- */
import InternMaster from './pages/roster/InternMaster';
import ActivitiesManager from './pages/admin/ActivitiesManager';

/* ---------- Admin: User Management (NEW) ---------- */
import UserManagement from './pages/admin/UserManagement';

// Role hierarchy baseline (Sep 2026):
// - Intern access remains self-service only.
// - JR uses the current reviewer baseline; JR-1/JR-2/JR-3 differentiation is intentionally TBD.
// - SR/Faculty/HOD retain reviewer/oversight access within the tenant.
// - Roster review may be shared, but tenant administration and Intern Master maintenance are Admin/Super Admin only.
// No regression: existing full role strings still work; common short aliases also work.
const ROSTER_ROLES = new Set([
  'jr',
  'junior resident',
  'sr',
  'senior resident',
  'faculty',
  'admin',
  'administrator',
  'hod',
  'head of department',
  'owner',
  'super admin',
]);

const ADMIN_ROLES = new Set([
  'admin',
  'administrator',
  'super admin',
  'superadmin',
  'cmfm admin',
  'admin user',
]);

function safeJsonParse(v) {
  try {
    return JSON.parse(v || 'null');
  } catch {
    return null;
  }
}

function normalizeRoleToken(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getStoredRoleLower() {
  const keys = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  try {
    for (const key of keys) {
      const fromLocal = safeJsonParse(localStorage.getItem(key));
      const fromSession = safeJsonParse(sessionStorage.getItem(key));
      const found = [fromLocal, fromSession].find(Boolean);
      const role =
        found?.role ||
        found?.user?.role ||
        found?.profile?.role ||
        found?.userType ||
        found?.user?.userType ||
        '';
      if (role) return normalizeRoleToken(role);
    }

    const fallback =
      localStorage.getItem('role') ||
      localStorage.getItem('userRole') ||
      sessionStorage.getItem('role') ||
      sessionStorage.getItem('userRole') ||
      '';

    return normalizeRoleToken(fallback);
  } catch {
    return '';
  }
}

function isRosterRole(role) {
  const normalized = normalizeRoleToken(role);
  if (ROSTER_ROLES.has(normalized)) return true;

  return (
    normalized.includes('junior resident') ||
    normalized.includes('senior resident') ||
    normalized.includes('faculty') ||
    normalized.includes('admin') ||
    normalized.includes('hod') ||
    normalized.includes('owner')
  );
}

function isAdminRole(role) {
  const normalized = normalizeRoleToken(role);
  if (ADMIN_ROLES.has(normalized)) return true;
  return normalized.endsWith(' admin');
}

function readTenantSlugFromStorage() {
  try {
    const candidates = [
      localStorage.getItem('activeTenantSlug'),
      localStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    return candidates.find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
  } catch {
    return '';
  }
}

function getTenantPrefixFromPath() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    if (m) return m[0];

    const slug = readTenantSlugFromStorage();
    return slug ? `/t/${slug}` : '';
  } catch {
    return '';
  }
}

function RosterOnly({ children }) {
  const role = getStoredRoleLower();
  if (isRosterRole(role)) return children;

  const tp = getTenantPrefixFromPath();
  return <Navigate to={tp ? `${tp}/dashboard` : '/dashboard'} replace />;
}

function AdminOnly({ children }) {
  const role = getStoredRoleLower();
  if (isAdminRole(role)) return children;

  const tp = getTenantPrefixFromPath();
  return <Navigate to={tp ? `${tp}/dashboard` : '/dashboard'} replace />;
}

const THEME_KEY = 'ui_theme_mode';
function getInitialMode() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {}
  if (document.documentElement.classList.contains('dark')) return 'dark';
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

/** /t/:tenantSlug → /t/:tenantSlug/cmfm/login */
function TenantIndexRedirect() {
  const { tenantSlug } = useParams();
  return <Navigate to={`/t/${tenantSlug}/cmfm/login`} replace />;
}

/** Alias: /t/:tenantSlug/cmfm-login → /t/:tenantSlug/cmfm/login */
function CMFMLoginAliasRedirect() {
  const { tenantSlug } = useParams();
  return <Navigate to={`/t/${tenantSlug}/cmfm/login`} replace />;
}

/** Alias: /t/:tenantSlug/login-qr OR /t/:tenantSlug/cmfm-login-qr → /t/:tenantSlug/cmfm/login-qr */
function CMFMLoginQRAliasRedirect() {
  const { tenantSlug } = useParams();
  return <Navigate to={`/t/${tenantSlug}/cmfm/login-qr`} replace />;
}

/** Add Citizen wrapper: keeps merged flow inside Comprehensive Services after registration */
function AddCitizenRoutePage() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();

  const operationsPath = tenantSlug
    ? `/t/${tenantSlug}/dashboard/operations`
    : '/dashboard/operations';

  return (
    <CitizenRegistrationForm
      onRegistered={(nextState) => {
        navigate(operationsPath, {
          replace: true,
          state: nextState,
        });
      }}
    />
  );
}

/** Legacy encounter URLs with a Visit ID now converge on the canonical Workspace. */
function LegacyEncounterBridge({ section, fallback }) {
  const { citizenId = '' } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search || '');
  const visitId = String(query.get('visitId') || location.state?.visitId || '').trim();

  if (!visitId) return fallback;

  const tenantMatch = String(location.pathname || '').match(/^\/t\/[^/]+/);
  const tenantPrefix = tenantMatch ? tenantMatch[0] : '';
  const next = new URLSearchParams();
  next.set('section', section || 'history');
  if (query.get('mode') === 'readonly' || location.state?.readOnly === true) {
    next.set('mode', 'readonly');
  }
  const from = query.get('from');
  if (from) next.set('from', from);

  const target = `${tenantPrefix}/citizen/${encodeURIComponent(citizenId)}/visit/${encodeURIComponent(visitId)}/workspace?${next.toString()}`;
  return <Navigate to={target} replace state={location.state} />;
}

/** Root redirect: localhost → default tenant, production → owner (no regression) */
function RootRedirect() {
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    const hostWithPort = String(window.location.host || "").toLowerCase();

    // Local dev: keep previous behavior (default tenant)
    const isLocal =
      /^localhost(:\d+)?$/.test(hostWithPort) || host === "127.0.0.1" || host === "localhost";
    if (isLocal) {
      const slug = "aiimsjodhpur-cmfm";
      return <Navigate to={`/t/${slug}/cmfm/login`} replace />;
    }

    // Host-based entrypoints (custom subdomains)
    // - owner.mededtek.in  -> Owner portal
    // - cmfm.mededtek.in   -> Tenant CMFM login (default or last-used tenant)
    const isOwnerHost = host.startsWith("owner.");
    if (isOwnerHost) {
      return <Navigate to="/owner/login" replace />;
    }

    const isCmfmHost = host.startsWith("cmfm.");
    if (isCmfmHost) {
      let slug = "aiimsjodhpur-cmfm";

      // Prefer last-used tenant slug if available (safe characters only)
      try {
        const candidates = [
          localStorage.getItem("activeTenantSlug"),
          localStorage.getItem("tenantSlug"),
          sessionStorage.getItem("activeTenantSlug"),
          sessionStorage.getItem("tenantSlug"),
        ]
          .map((v) => String(v || "").trim())
          .filter(Boolean);

        const safe = candidates.find((s) => /^[a-z0-9-]{2,80}$/i.test(s));
        if (safe) slug = safe;
      } catch {}

      return <Navigate to={`/t/${slug}/cmfm/login`} replace />;
    }
  } catch {}

  // Default (no regression)
  return <Navigate to="/owner/login" replace />;
}

/** Catch-all redirect that preserves tenant if present */
function NotFoundRedirect() {
  const tp = getTenantPrefixFromPath();
  return <Navigate to={tp ? `${tp}/cmfm/login` : '/owner/login'} replace />;
}

export default function App() {
  const [mode, setMode] = useState(getInitialMode());

  /* Normalize accidental /t/<slug>/owner/... → /owner/... */
  useEffect(() => {
    try {
      const path = window.location.pathname || '';
      const hit = path.match(/^\/t\/[^/]+(\/owner(?:\/.*)?)/);
      if (hit) {
        const suffix = hit[1] || '/owner/login';
        const next = `${suffix}${window.location.search || ''}${window.location.hash || ''}`;
        window.location.replace(next);
      }
    } catch {}
  }, []);

  /* If no /t/<slug> on local dev and not on owner route, push to default tenant */
  useEffect(() => {
    try {
      const path = window.location.pathname || '';
      const isOwner = /^\/owner(\/|$)/.test(path);
      const hasTenant = /^\/t\/[^/]+/.test(path);
      const isLocal = /^localhost(:\d+)?$/.test(window.location.host);
      if (!isOwner && !hasTenant && isLocal) {
        const slug = 'aiimsjodhpur-cmfm';
        const next = `/t/${slug}${path}${window.location.search}${window.location.hash}`;
        window.location.replace(next);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    try {
      localStorage.setItem(THEME_KEY, mode);
    } catch {}
  }, [mode]);

  useEffect(() => {
    const ob = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      const newMode = isDark ? 'dark' : 'light';
      setMode((prev) => (prev !== newMode ? newMode : prev));
      try {
        localStorage.setItem(THEME_KEY, newMode);
      } catch {}
    });
    ob.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => ob.disconnect();
  }, []);

  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrandingProvider>
        <CitizenProvider>
          {/* ✅ NO basename: prevents /t/slug duplication */}
          <Router>
            <CitizenRouteSync>
              <Routes>
                {/* Root public */}
                <Route path="/" element={<RootRedirect />} />

                {/* Owner auth (root only) */}
                <Route path="/owner/login" element={<OwnerLogin />} />
                <Route
                  path="/owner"
                  element={
                    <ProtectedOwnerRoute>
                      <OwnerHome />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/subjects"
                  element={
                    <ProtectedOwnerRoute>
                      <SubjectsHome />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/subjects/:subjectSlug"
                  element={
                    <ProtectedOwnerRoute>
                      <SubjectDetail />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/colleges"
                  element={
                    <ProtectedOwnerRoute>
                      <CollegesHome />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/colleges/:slug/subjects"
                  element={
                    <ProtectedOwnerRoute>
                      <CollegeSubjects />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/tenants"
                  element={
                    <ProtectedOwnerRoute>
                      <TenantsList />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/tenants/new"
                  element={
                    <ProtectedOwnerRoute>
                      <TenantEdit />
                    </ProtectedOwnerRoute>
                  }
                />
                <Route
                  path="/owner/tenants/:slug"
                  element={
                    <ProtectedOwnerRoute>
                      <TenantEdit />
                    </ProtectedOwnerRoute>
                  }
                />

                {/* ---------------- Tenant PUBLIC routes (preferred) ---------------- */}
                <Route path="/t/:tenantSlug" element={<TenantIndexRedirect />} />

                {/* ✅ Standardized CMFM login */}
                <Route path="/t/:tenantSlug/cmfm/login" element={<CMFMLoginRegister />} />

                {/* ✅ Alias redirects (old links) */}
                <Route path="/t/:tenantSlug/cmfm-login" element={<CMFMLoginAliasRedirect />} />

                {/* ✅ Standardized QR login */}
                <Route path="/t/:tenantSlug/cmfm/login-qr" element={<CMFMLoginQR />} />

                {/* ✅ QR alias redirects */}
                <Route path="/t/:tenantSlug/login-qr" element={<CMFMLoginQRAliasRedirect />} />
                <Route path="/t/:tenantSlug/cmfm-login-qr" element={<CMFMLoginQRAliasRedirect />} />

                {/* Backward compatible public routes (non-tenant; still allowed) */}
                <Route path="/cmfm/login" element={<CMFMLoginRegister />} />
                <Route path="/cmfm-login" element={<Navigate to="/cmfm/login" replace />} />

                <Route path="/cmfm/login-qr" element={<CMFMLoginQR />} />
                <Route path="/login-qr" element={<Navigate to="/cmfm/login-qr" replace />} />

                <Route path="/portal" element={<Navigate to="/dashboard/citizen" replace />} />

                {/* ---------------- Tenant PROTECTED routes ---------------- */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/t/:tenantSlug/*" element={<LayoutWithSidebarAndHeader />}>
                    {/* Landing */}
                    <Route path="dashboard" element={<DashboardLanding />} />

                    {/* Groups */}
                    <Route path="dashboard/clinical" element={<GroupClinical />} />
                    <Route path="dashboard/operations" element={<GroupOperations />} />
                    <Route path="dashboard/telemedicine" element={<GroupTelemedicine />} />
                    <Route path="dashboard/academics" element={<GroupAcademics />} />
                    <Route path="dashboard/citizen" element={<GroupCitizen />} />

                    {/* Clinical Services */}
                    <Route path="dashboard/clinical/geriatric" element={<GeriatricClinic />} />
                    <Route path="dashboard/clinical/adolescent" element={<AdolescentClinic />} />
                    <Route path="dashboard/clinical/camps" element={<MedicalCamps />} />
                    <Route path="dashboard/clinical/occupational" element={<OccupationalHealth />} />
                    <Route path="dashboard/clinical/rmnch" element={<RMNCHServices />} />
                    <Route path="dashboard/clinical/report" element={<ClinicalServicesReport />} />

                    {/* LGBTQIA+ */}
                    <Route path="dashboard/clinical/lgbtqia" element={<GroupLGBTQIA />} />
                    <Route path="dashboard/clinical/lgbtqia/intake" element={<LGBTQIAIntakeForm />} />
                    <Route path="dashboard/clinical/lgbtqia/care-bundle" element={<LGBTQIACareBundle />} />
                    <Route path="dashboard/clinical/lgbtqia/resources" element={<LGBTQIAResources />} />

                    {/* Add citizen */}
                    <Route path="dashboard/add-citizen" element={<AddCitizenRoutePage />} />

                    {/* Legacy + preferred encounter routes */}
                    <Route path="citizen/:citizenId/medical-history" element={<CitizenMedicalHistory />} />
                    <Route path="citizen/:citizenId/examination" element={<CitizenExam />} />
                    <Route path="citizen/:citizenId/investigations" element={<CitizenInvestigations />} />
                    <Route path="citizen/:citizenId/prescriptions" element={<CitizenPrescriptions />} />
                    <Route path="citizen/:citizenId/prescription" element={<CitizenPrescriptions />} />

                    <Route path="citizen/:citizenId/encounter/history" element={<LegacyEncounterBridge section="history" fallback={<CitizenMedicalHistory />} />} />
                    <Route path="citizen/:citizenId/encounter/examination" element={<LegacyEncounterBridge section="examination" fallback={<CitizenExam />} />} />
                    <Route path="citizen/:citizenId/encounter/investigations" element={<LegacyEncounterBridge section="investigations" fallback={<CitizenInvestigations />} />} />
                    <Route path="citizen/:citizenId/encounter/prescriptions" element={<LegacyEncounterBridge section="prescription" fallback={<CitizenPrescriptions />} />} />

                    {/* Visits */}
                    <Route path="citizen/:citizenId/add-visit" element={<AddVisit />} />
                    <Route path="citizen/:citizenId/visit/:visitId/workspace" element={<VisitWorkspace />} />
                    <Route path="citizen/:citizenId/encounter" element={<VisitDetails />} />
                    <Route path="citizen/:citizenId/encounter/summary" element={<VisitSummary />} />
                    <Route path="citizen/:citizenId/visit-summary" element={<VisitSummary />} />
                    <Route path="citizen/:citizenId/previous-visits" element={<PreviousVisitsList />} />
                    <Route path="citizen/:citizenId/social-household-context" element={<SocialHouseholdContext />} />
                    <Route path="citizen/:citizenId/upload-documents" element={<CitizenUploadDocuments />} />

                    {/* DEO */}
                    <Route path="deo-dashboard" element={<DEODashboard />} />
                    <Route path="deo/visit/:visitId" element={<DEOVisitWorkflow />} />

                    {/* Approvals */}
                    <Route path="dashboard/approvals" element={<ApprovalsDashboard />} />
                    <Route path="dashboard/approval" element={<ApprovalsDashboard />} />
                    <Route path="dashboard/approvals/returned-edit" element={<ApprovalEditor />} />
                    <Route path="approvals" element={<Navigate to="dashboard/approvals" replace />} />
                    <Route path="approval" element={<Navigate to="dashboard/approvals" replace />} />
                    <Route path="approvals/returned-edit" element={<Navigate to="dashboard/approvals/returned-edit" replace />} />

                    {/* Profiles */}
                    <Route path="profile/me" element={<ProfileMe />} />
                    <Route path="profile/:userId" element={<ProfileEditor />} />

                    {/* Intern module */}
                    <Route path="intern/activity" element={<ActivityLog />} />
                    <Route path="intern/activity/log" element={<ActivityLog />} />
                    <Route path="intern/activity/returned" element={<ReturnedActivities />} />
                    <Route path="intern/activity/returned-guided" element={<ReturnedActivities />} />
                    <Route path="intern/activity/returns" element={<ReturnedActivities />} />
                    <Route path="intern/self-eval" element={<PerformanceSelfEval />} />
                    <Route path="intern/roster" element={<RosterView />} />
                    <Route path="intern/review" element={<InternReviewQueue />} />

                    {/* SR→Faculty performance review */}
                    <Route path="intern/perf/new" element={<PerformanceReviewNew />} />
                    <Route path="intern/perf/review" element={<PerformanceReviewQueue />} />
                    <Route path="intern/perf/review/:id" element={<PerformanceReviewDetail />} />

                    {/* Guided Activity flow */}
                    <Route path="intern/activity/new" element={<ActivityStartWizard />} />
                    <Route path="intern/activity/start" element={<ActivityStartWizard />} />
                    <Route path="intern/activity/launcher" element={<ActivityStartWizard />} />
                    <Route path="intern/activity/run/:id" element={<ActivityGuidedRun />} />
                    {/* ✅ Aliases prevent NotFound redirects from older/newer buttons */}
                    <Route path="intern/activity/guided/:id" element={<ActivityGuidedRun />} />
                    <Route path="intern/activity/continue/:id" element={<ActivityGuidedRun />} />
                    <Route path="intern/activity/edit/:id" element={<ActivityGuidedRun />} />
                    <Route path="intern/activity/observe/:id" element={<ObserverSignoff />} />
                    <Route path="intern/activity/evidence/:id" element={<ObserverSignoff />} />
                    <Route path="intern/activity/review/:id" element={<ObserverSignoff />} />
                    <Route path="intern/activity/observer/:id" element={<ObserverSignoff />} />
                    <Route path="intern/activity/signoff/:id" element={<ObserverSignoff />} />
                    <Route path="intern/activity/approval/:id" element={<ObserverSignoff />} />
                    <Route path="dashboard/approval/activity/:id" element={<ObserverSignoff />} />
                    <Route path="dashboard/approvals/activity/:id" element={<ObserverSignoff />} />

                    {/* Guidelines */}
                    <Route path="intern/guidelines" element={<GuidelinesLibrary />} />
                    <Route path="guidelines" element={<GuidelinesLibrary />} />

                    {/* Ops pages */}
                    <Route path="dashboard/operations/chc" element={<CHCCoordination />} />
                    <Route path="dashboard/operations/centre-board" element={<CentreBoard />} />
                    <Route path="dashboard/operations/daily-opd-census" element={<DailyOPDCensus />} />

                    {/* Intern/Admin manager screens (role-guarded) */}
                    <Route path="intern/manager" element={<RosterOnly><RosterManager /></RosterOnly>} />
                    <Route path="admin/roster" element={<RosterOnly><RosterManager /></RosterOnly>} />
                    <Route path="intern/master" element={<AdminOnly><InternMaster /></AdminOnly>} />
                    <Route path="admin/activities" element={<AdminOnly><ActivitiesManager /></AdminOnly>} />
                    {/* ✅ NEW: Admin → Users (role-guarded) */}
                    <Route path="admin/users" element={<AdminOnly><UserManagement /></AdminOnly>} />
                    <Route path="dashboard/admin/users" element={<AdminOnly><UserManagement /></AdminOnly>} />
                  </Route>
                </Route>

                {/* ---------------- Non-tenant PROTECTED routes (backward compatible) ---------------- */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<LayoutWithSidebarAndHeader />}>
                    <Route path="/dashboard" element={<DashboardLanding />} />

                    <Route path="/dashboard/clinical" element={<GroupClinical />} />
                    <Route path="/dashboard/operations" element={<GroupOperations />} />
                    <Route path="/dashboard/telemedicine" element={<GroupTelemedicine />} />
                    <Route path="/dashboard/academics" element={<GroupAcademics />} />
                    <Route path="/dashboard/citizen" element={<GroupCitizen />} />

                    <Route path="/dashboard/clinical/geriatric" element={<GeriatricClinic />} />
                    <Route path="/dashboard/clinical/adolescent" element={<AdolescentClinic />} />
                    <Route path="/dashboard/clinical/camps" element={<MedicalCamps />} />
                    <Route path="/dashboard/clinical/occupational" element={<OccupationalHealth />} />
                    <Route path="/dashboard/clinical/rmnch" element={<RMNCHServices />} />
                    <Route path="/dashboard/clinical/report" element={<ClinicalServicesReport />} />

                    <Route path="/dashboard/clinical/lgbtqia" element={<GroupLGBTQIA />} />
                    <Route path="/dashboard/clinical/lgbtqia/intake" element={<LGBTQIAIntakeForm />} />
                    <Route path="/dashboard/clinical/lgbtqia/care-bundle" element={<LGBTQIACareBundle />} />
                    <Route path="/dashboard/clinical/lgbtqia/resources" element={<LGBTQIAResources />} />

                    <Route path="/dashboard/add-citizen" element={<AddCitizenRoutePage />} />

                    <Route path="/citizen/:citizenId/medical-history" element={<CitizenMedicalHistory />} />
                    <Route path="/citizen/:citizenId/examination" element={<CitizenExam />} />
                    <Route path="/citizen/:citizenId/investigations" element={<CitizenInvestigations />} />
                    <Route path="/citizen/:citizenId/prescriptions" element={<CitizenPrescriptions />} />
                    <Route path="/citizen/:citizenId/prescription" element={<CitizenPrescriptions />} />

                    <Route path="/citizen/:citizenId/encounter/history" element={<LegacyEncounterBridge section="history" fallback={<CitizenMedicalHistory />} />} />
                    <Route path="/citizen/:citizenId/encounter/examination" element={<LegacyEncounterBridge section="examination" fallback={<CitizenExam />} />} />
                    <Route path="/citizen/:citizenId/encounter/investigations" element={<LegacyEncounterBridge section="investigations" fallback={<CitizenInvestigations />} />} />
                    <Route path="/citizen/:citizenId/encounter/prescriptions" element={<LegacyEncounterBridge section="prescription" fallback={<CitizenPrescriptions />} />} />

                    <Route path="/citizen/:citizenId/add-visit" element={<AddVisit />} />
                    <Route path="/citizen/:citizenId/visit/:visitId/workspace" element={<VisitWorkspace />} />
                    <Route path="/citizen/:citizenId/encounter" element={<VisitDetails />} />
                    <Route path="/citizen/:citizenId/encounter/summary" element={<VisitSummary />} />
                    <Route path="/citizen/:citizenId/visit-summary" element={<VisitSummary />} />
                    <Route path="/citizen/:citizenId/previous-visits" element={<PreviousVisitsList />} />
                    <Route path="/citizen/:citizenId/social-household-context" element={<SocialHouseholdContext />} />
                    <Route path="/citizen/:citizenId/upload-documents" element={<CitizenUploadDocuments />} />

                    <Route path="/deo-dashboard" element={<DEODashboard />} />
                    <Route path="/deo/visit/:visitId" element={<DEOVisitWorkflow />} />

                    <Route path="/dashboard/approvals" element={<ApprovalsDashboard />} />
                    <Route path="/dashboard/approval" element={<ApprovalsDashboard />} />
                    <Route path="/dashboard/approvals/returned-edit" element={<ApprovalEditor />} />

                    <Route path="/profile/me" element={<ProfileMe />} />
                    <Route path="/profile/:userId" element={<ProfileEditor />} />

                    <Route path="/intern/activity" element={<ActivityLog />} />
                    <Route path="/intern/activity/log" element={<ActivityLog />} />
                    <Route path="/intern/activity/returned" element={<ReturnedActivities />} />
                    <Route path="/intern/activity/returned-guided" element={<ReturnedActivities />} />
                    <Route path="/intern/activity/returns" element={<ReturnedActivities />} />
                    <Route path="/intern/self-eval" element={<PerformanceSelfEval />} />
                    <Route path="/intern/roster" element={<RosterView />} />
                    <Route path="/intern/review" element={<InternReviewQueue />} />

                    <Route path="/intern/perf/new" element={<PerformanceReviewNew />} />
                    <Route path="/intern/perf/review" element={<PerformanceReviewQueue />} />
                    <Route path="/intern/perf/review/:id" element={<PerformanceReviewDetail />} />

                    <Route path="/intern/activity/new" element={<ActivityStartWizard />} />
                    <Route path="/intern/activity/start" element={<ActivityStartWizard />} />
                    <Route path="/intern/activity/launcher" element={<ActivityStartWizard />} />
                    <Route path="/intern/activity/run/:id" element={<ActivityGuidedRun />} />
                    {/* ✅ Aliases prevent NotFound redirects from older/newer buttons */}
                    <Route path="/intern/activity/guided/:id" element={<ActivityGuidedRun />} />
                    <Route path="/intern/activity/continue/:id" element={<ActivityGuidedRun />} />
                    <Route path="/intern/activity/edit/:id" element={<ActivityGuidedRun />} />
                    <Route path="/intern/activity/observe/:id" element={<ObserverSignoff />} />
                    <Route path="/intern/activity/evidence/:id" element={<ObserverSignoff />} />
                    <Route path="/intern/activity/review/:id" element={<ObserverSignoff />} />
                    <Route path="/intern/activity/observer/:id" element={<ObserverSignoff />} />
                    <Route path="/intern/activity/signoff/:id" element={<ObserverSignoff />} />
                    <Route path="/intern/activity/approval/:id" element={<ObserverSignoff />} />
                    <Route path="/dashboard/approval/activity/:id" element={<ObserverSignoff />} />
                    <Route path="/dashboard/approvals/activity/:id" element={<ObserverSignoff />} />

                    <Route path="/intern/guidelines" element={<GuidelinesLibrary />} />
                    <Route path="/guidelines" element={<GuidelinesLibrary />} />

                    <Route path="/dashboard/operations/chc" element={<CHCCoordination />} />
                    <Route path="/dashboard/operations/centre-board" element={<CentreBoard />} />
                    <Route path="/dashboard/operations/daily-opd-census" element={<DailyOPDCensus />} />

                    <Route path="/intern/manager" element={<RosterOnly><RosterManager /></RosterOnly>} />
                    <Route path="/admin/roster" element={<RosterOnly><RosterManager /></RosterOnly>} />
                    <Route path="/intern/master" element={<AdminOnly><InternMaster /></AdminOnly>} />
                    <Route path="/admin/activities" element={<AdminOnly><ActivitiesManager /></AdminOnly>} />
                    {/* ✅ NEW: Admin → Users (role-guarded) */}
                    <Route path="/admin/users" element={<AdminOnly><UserManagement /></AdminOnly>} />
                    <Route path="/dashboard/admin/users" element={<AdminOnly><UserManagement /></AdminOnly>} />
                  </Route>
                </Route>

                {/* Aliases */}
                <Route path="/approvals" element={<Navigate to="/dashboard/approvals" replace />} />
                <Route path="/approval" element={<Navigate to="/dashboard/approvals" replace />} />
                <Route path="/approvals/returned-edit" element={<Navigate to="/dashboard/approvals/returned-edit" replace />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFoundRedirect />} />
              </Routes>
            </CitizenRouteSync>
          </Router>
        </CitizenProvider>
      </BrandingProvider>
    </ThemeProvider>
  );
}
